import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Toaster, toast } from "sonner";
import {
  Zap, Shield, Brain, Dna, Radio, AlertTriangle,
  ChevronDown, Send, Mail, Menu, X, ExternalLink,
  BookOpen, MessageCircle, ArrowUp, Users
} from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CommunityPage from "@/pages/CommunityPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── TOAST OPTIONS (stable reference) ─── */
const TOAST_OPTIONS = {
  style: {
    background: "#121212",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
    borderRadius: "0",
  },
};

/* ─── IMAGES ─── */
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1ef12e45-b695-4730-9aa3-aa8b938985e3/images/853884dffe19514a38baf0fa2124c17ff337e8a576de81d153266bb789b19179.png";
const DNA_IMG = "https://static.prod-images.emergentagent.com/jobs/1ef12e45-b695-4730-9aa3-aa8b938985e3/images/557e0155f228a7584b524e4cb0d6236f6dc00243697a14c60e31d0e12f634972.png";
const NERVOUS_IMG = "https://static.prod-images.emergentagent.com/jobs/1ef12e45-b695-4730-9aa3-aa8b938985e3/images/3a5d54f8a77df4f5cc532070a65e8d33b28f8690b60114001bacf26bcfa6fd84.png";
const CTA_BG = "https://static.prod-images.emergentagent.com/jobs/1ef12e45-b695-4730-9aa3-aa8b938985e3/images/6fffb67c7a5dff1e09f8f5168ae85a4bf32f38fa459bbef0e556288d89defec3.png";
const TOWER_IMG = "https://images.unsplash.com/photo-1766654607777-09372ec0e6eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxjZWxsJTIwdG93ZXIlMjB0cmFuc21pc3Npb258ZW58MHx8fHwxNzc2MjU2MTM1fDA&ixlib=rb-4.1.0&q=85";

/* ─── NAV LINKS ─── */
const NAV_LINKS = [
  { label: "MY STORY", href: "#story" },
  { label: "THE SCIENCE", href: "#science" },
  { label: "RESOURCES", href: "#resources" },
  { label: "FAQ", href: "#faq" },
  { label: "COMMUNITY", href: "/community", isRoute: true },
  { label: "CONTACT", href: "#contact" },
];

/* ═══════════════════════════════════════════════════════ */
/*  HEADER                                                 */
/* ═══════════════════════════════════════════════════════ */
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // handleScroll defined inside effect; setScrolled is stable

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#story" className="flex items-center gap-2 group" data-testid="logo-link">
          <Zap className="w-6 h-6 text-[#FF3B30] group-hover:scale-110 transition-transform" />
          <span className="font-['Anton'] text-xl uppercase tracking-wider text-white">
            EMF<span className="text-[#FF3B30]">AWARE</span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) =>
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 hover:text-[#FF3B30] transition-colors duration-300"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 hover:text-[#FF3B30] transition-colors duration-300"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <nav
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-4"
          data-testid="mobile-nav"
        >
          {NAV_LINKS.map((link) =>
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm uppercase tracking-[0.2em] font-bold text-white/70 hover:text-[#FF3B30] transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm uppercase tracking-[0.2em] font-bold text-white/70 hover:text-[#FF3B30] transition-colors"
              >
                {link.label}
              </a>
            )
          )}
        </nav>
      )}
    </header>
  );
};

/* ═══════════════════════════════════════════════════════ */
/*  HERO / PERSONAL STORY                                  */
/* ═══════════════════════════════════════════════════════ */
const HeroSection = () => (
  <section
    id="story"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-start overflow-hidden"
  >
    {/* Background */}
    <div className="absolute inset-0">
      <img
        src={HERO_IMG}
        alt="EMF waves"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-6 animate-fade-in-up">
          Personal Encounter
        </p>
        <h1
          className="font-['Anton'] text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9] text-white mb-8 animate-fade-in-up animation-delay-100"
          data-testid="hero-title"
        >
          THE INVISIBLE
          <br />
          <span className="text-[#FF3B30]">THREAT</span> TO YOUR
          <br />
          BIOLOGY
        </h1>
        <p className="text-base sm:text-lg leading-relaxed text-white/70 max-w-xl mb-10 animate-fade-in-up animation-delay-200">
          This is my story of living under electromagnetic field attack &mdash;
          an experience that forever changed how I understand the unseen forces
          surrounding us. What I share here is deeply personal, yet grounded in
          science that affects every single one of us.
        </p>
        <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
          <a href="#science" data-testid="hero-cta-science">
            <Button className="rounded-none uppercase tracking-widest font-bold border-2 border-[#FF3B30] bg-[#FF3B30] text-white hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 px-8 py-6 text-sm">
              Discover the Science
            </Button>
          </a>
          <a href="#contact" data-testid="hero-cta-contact">
            <Button className="rounded-none uppercase tracking-widest font-bold border-2 border-white/30 bg-transparent text-white hover:border-[#FFCC00] hover:text-[#FFCC00] transition-all duration-300 px-8 py-6 text-sm">
              Share Your Story
            </Button>
          </a>
        </div>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
      <span className="text-xs uppercase tracking-[0.2em] text-white/40">Scroll</span>
      <ChevronDown className="w-5 h-5 text-white/40" />
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════ */
/*  PERSONAL STORY DETAIL                                  */
/* ═══════════════════════════════════════════════════════ */
const StorySection = () => (
  <section className="relative py-24 md:py-32 grain-overlay" data-testid="story-detail-section">
    <div className="relative z-10 max-w-4xl mx-auto px-6">
      <div className="border border-white/10 p-8 md:p-12">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
          My Story
        </p>
        <h2 className="font-['Anton'] text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white mb-8">
          LIVING UNDER <span className="text-[#FFCC00]">EMF ATTACK</span>
        </h2>
        <div className="space-y-6 text-base leading-relaxed text-white/70">
          <p>
            Hello friends, this is Benjamin Zhang.  Here is my personal encounter with DNA-targeted EMF attack. Since Spring 2025, I was attacked by EMF beam.  The symptoms include: body shock causing waking up around 1AM to 3AM; acute body pain like needle's pricks; swallowing difficulty; once my pupils dilated and could not contract even in strong light, as a result the environment was too bright and I could not see anything clearly.  The neural system showed signs of degeneration. This completely disrupted my life.
          </p>
          <p>
            In the beginning of 2025, I noticed I often woke up around 1AM to 3AM after a strong body shock. That disrupted my sleep which was very bad for my long term health. So I tried to adjust my life, food, exercise, sleep time etc.  After taking care of all of these, it still could happen.  I figured it might be related to environment issues. So I got Geiger Counter, EMF meter, Air Quality Monitor, and tried to move around locations and environment factors, turned off Wifi at home, shielded potential EMF sources within my reach, tried to sleep inside a Faraday Cage in order to tell what might be the cause(s). After controlling all these environment factors, it could still happen if I slept at home without shielding at night. Now the only uncontrollable factor with significant energy was the 5G cell tower.  Well if that was the case, it needed to know where I was located. So I left my cell phone at home and powered off the SOS system inside my car, I drove out during night sleep time and park randomly anywhere quiet, just slept inside the car.  That was very helpful for my body's recovery even if the sleep condition was not ideal.
          </p>
          <p>
            In Summer 2025, I noticed the LED street light closest to my house sent out cool white color instead of warm brownish color as all other street lights did, and it also periodically flashed. That alone could send out EMF signals.  Within 2 days, my body felt the acute pain like needle's pricks as well as other signs of sickness, and I developed swallow difficulty.  If I stay behind multiple houses from the street light, I didn't feel that pain; if I stayed close to the street light without blocks, I could feel the pain very quickly.  Now I had to drive out around 7PM as soon as the street light was on.  At one night, I had to come back to fix my IT system.  I was so tired and fell asleep heading toward the street light direction. When I woke up, I noticed my phone screen was too bright I could not read the words on the screen.  I felt panicked and figured it might be retina issue, so I visited my eye doctor immediately.  The diagnosis showed my retina did not have obvious problem.  I looked into the mirror and figured out my pupils were fully dilated and could not contract even in an environment with strong light.  Recall what had happened to my body in the past, I realized it was the LED street light.  The acute body pain like needle's pricks was the sign of neural damage, the neural damage caused jaw muscle not function well thus swallow difficulty, the neural damage caused my eye muscle not function well thus my pupils could not contract.  The whole body's neural system was degenerating.  When the street light is on, I have to leave home asap. I cannot sleep at home till today.
          </p>
          <p>
            The fact this only affects me not any of my other family members, means this attack is DNA targeted. When I slept in the car, I was wondering "Am I the only one being attacked?".  After having parked in so many communities, my observation shows it was not just me.  Most people's DNA were already collected through our medical system; 5G cell towers and LED street lights are everywhere and are still deploying more and more.  These billions DNA records, millions 5G cell towers, billions LED street lights are not just for me, one person. This is a life-threatening and potentially mass murdering risk lurking in every community. Nothing is important, nothing is more urgent than this. We cannot solve a problem without being aware of the problem.
          </p>
          <p>
            Please spread this message far and wide to raise public's awareness!!!  That is the first step to remove this life-threat from our reality. If you have something to share and want to work together on this threat, feel free to reach out with message at the end of the page. Thank you for your attention!
          </p>
        </div>
        <Separator className="my-8 bg-white/10" />
        <div className="space-y-4">
          <p className="text-base leading-relaxed text-white/70 font-semibold">
            You may check out the videos:
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://www.youtube.com/@emfaware"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-[#FF0000]/50 text-[#FF0000] text-sm uppercase tracking-widest font-bold hover:bg-[#FF0000] hover:text-white transition-all duration-300"
              data-testid="link-youtube"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              EMFAware at YouTube
            </a>
            <a
              href="https://rumble.com/emfaware"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-[#85c742]/50 text-[#85c742] text-sm uppercase tracking-widest font-bold hover:bg-[#85c742] hover:text-black transition-all duration-300"
              data-testid="link-rumble"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M16.5 12l-7 4.5V7.5l7 4.5z"/><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
              EMFAware at Rumble
            </a>
            <a
              href="https://www.tiktok.com/@emfaware"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-white/30 text-white/70 text-sm uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-all duration-300"
              data-testid="link-tiktok"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.49a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.27 8.27 0 0 0 4.76 1.5v-3.4a4.85 4.85 0 0 1-1-.12z"/></svg>
              EMFAware at TikTok
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════ */
/*  MARQUEE BANNER                                         */
/* ═══════════════════════════════════════════════════════ */
const MarqueeBanner = () => (
  <div className="overflow-hidden bg-[#FF3B30] py-3" data-testid="marquee-banner">
    <div className="animate-marquee whitespace-nowrap flex gap-12 items-center">
      {Array.from({ length: 10 }).map((_, i) => (
        <span key={`marquee-${i}`} className="font-['Anton'] text-2xl uppercase tracking-wider text-black flex items-center gap-4">
          <AlertTriangle className="w-5 h-5 inline" />
          INVISIBLE THREAT
          <span className="text-white/80">///</span>
          EMF EXPOSURE
          <span className="text-white/80">///</span>
          DNA DAMAGE
          <span className="text-white/80">///</span>
          PROTECT YOURSELF
        </span>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════ */
/*  SCIENCE SECTION                                        */
/* ═══════════════════════════════════════════════════════ */
const scienceCards = [
  {
    icon: <Dna className="w-8 h-8 text-[#FF3B30]" />,
    title: "EMF & DNA DAMAGE",
    image: DNA_IMG,
    points: [
      "Studies show that non-ionizing EMF radiation can cause single- and double-strand DNA breaks, similar to those caused by ionizing radiation (Lai & Singh, 1996).",
      "The REFLEX project (EU-funded, 2004) found that mobile phone-level EMF exposure caused genotoxic effects in human cells, including DNA fragmentation.",
      "EMF-induced oxidative stress generates reactive oxygen species (ROS), which directly damage DNA bases and disrupt repair mechanisms.",
      "Prolonged exposure may impair the body's natural DNA repair enzymes, increasing mutation accumulation over time."
    ]
  },
  {
    icon: <Brain className="w-8 h-8 text-[#FFCC00]" />,
    title: "EMF & THE NERVOUS SYSTEM",
    image: NERVOUS_IMG,
    points: [
      "The nervous system operates through electrical impulses. External EMFs can interfere with voltage-gated calcium channels (VGCCs), disrupting neural signaling (Pall, 2013).",
      "Activation of VGCCs leads to excess intracellular calcium, triggering nitric oxide and peroxynitrite production — linked to neuroinflammation and neurodegeneration.",
      "Research associates chronic EMF exposure with headaches, sleep disturbance, cognitive impairment, and increased blood-brain barrier permeability.",
      "The brain's pineal gland, which produces melatonin (our sleep hormone and antioxidant), is particularly sensitive to EMF disruption."
    ]
  },
  {
    icon: <Radio className="w-8 h-8 text-[#FF3B30]" />,
    title: "EMF & CELLULAR FUNCTION",
    points: [
      "Every cell in our body carries an electrical charge (~70mV membrane potential). External EMFs alter this delicate voltage balance, affecting cell-to-cell communication.",
      "EMF exposure triggers heat shock protein (HSP) responses — a cellular stress reaction normally reserved for trauma, infection, or extreme heat.",
      "Mitochondrial function can be disrupted, reducing ATP (energy) production and increasing free radical damage at the cellular level.",
      "The International Agency for Research on Cancer (IARC) classified RF-EMF as a Group 2B possible carcinogen (2011)."
    ]
  },
  {
    icon: <Shield className="w-8 h-8 text-[#FFCC00]" />,
    title: "EMF & THE IMMUNE SYSTEM",
    points: [
      "Chronic EMF exposure has been shown to alter cytokine profiles, shifting immune response toward inflammation (Szmigielski, 2013).",
      "Studies on occupational EMF exposure report changes in white blood cell counts and immunoglobulin levels.",
      "The stress response triggered by EMF increases cortisol production, which suppresses immune function when chronically elevated.",
      "Research suggests EMF may interfere with the body's circadian rhythm, weakening the immune system's nightly repair and regeneration cycle."
    ]
  }
];

const ScienceSection = () => (
  <section id="science" className="relative py-24 md:py-32" data-testid="science-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
          The Evidence
        </p>
        <h2 className="font-['Anton'] text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white mb-6">
          HOW EMF ATTACKS <span className="text-[#FFCC00]">YOUR BIOLOGY</span>
        </h2>
        <p className="text-base sm:text-lg leading-relaxed text-white/60 max-w-2xl">
          Electromagnetic fields interact with our bodies at the molecular level.
          Here is what peer-reviewed science tells us about these effects.
        </p>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10">
        {scienceCards.map((card) => (
          <div
            key={card.title}
            className="border border-white/10 p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300 group"
            data-testid={`science-card-${card.title}`}
          >
            {card.image && (
              <div className="mb-6 overflow-hidden h-48 -mx-8 -mt-8 md:-mx-10 md:-mt-10">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            )}
            <div className="flex items-center gap-4 mb-6 mt-2">
              {card.icon}
              <h3 className="font-['Anton'] text-xl sm:text-2xl uppercase tracking-tight text-white">
                {card.title}
              </h3>
            </div>
            <ul className="space-y-4">
              {card.points.map((point) => (
                <li key={point.slice(0, 50)} className="flex gap-3 text-sm leading-relaxed text-white/60">
                  <span className="text-[#FF3B30] font-bold mt-0.5 shrink-0">//</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════ */
/*  RESOURCES SECTION                                      */
/* ═══════════════════════════════════════════════════════ */
const resources = [
  {
    title: "BioInitiative Report (2012, Updated 2020)",
    desc: "Comprehensive review of over 1,800 studies on EMF health effects by independent scientists. Covers RF and ELF effects on DNA, cancer, neurology, and reproduction.",
    url: "https://bioinitiative.org"
  },
  {
    title: "IARC Monograph on RF-EMF (2013)",
    desc: "The WHO International Agency for Research on Cancer classified radiofrequency electromagnetic fields as Group 2B (possibly carcinogenic to humans).",
    url: "https://www.iarc.who.int"
  },
  {
    title: "NTP Study on Cell Phone Radiation (2018)",
    desc: "The U.S. National Toxicology Program found 'clear evidence' of tumors in male rats exposed to cell phone radiation, with DNA damage in both rats and mice.",
    url: "https://ntp.niehs.nih.gov"
  },
  {
    title: "Ramazzini Institute Study (2018)",
    desc: "Large-scale Italian study confirmed NTP findings at lower exposure levels, detecting increased rates of heart schwannomas and brain gliomas.",
    url: "https://www.ramazzini.org"
  },
  {
    title: "REFLEX Project — EU Funded (2004)",
    desc: "Multi-laboratory European study demonstrating genotoxic effects of mobile phone-level EMF on human cells, including DNA strand breaks.",
    url: "https://www.jrseco.com/eu-reflex-project"
  },
  {
    title: "Dr. Martin Pall — VGCC Research",
    desc: "Published research on voltage-gated calcium channel (VGCC) activation by EMFs, explaining the mechanism behind neurological, cardiac, and reproductive effects.",
    url: "https://pubmed.ncbi.nlm.nih.gov"
  }
];

const ResourcesSection = () => (
  <section id="resources" className="relative py-24 md:py-32 bg-[#0D0D0D]" data-testid="resources-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left column - intro */}
        <div className="lg:col-span-1">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            Research Library
          </p>
          <h2 className="font-['Anton'] text-3xl sm:text-4xl uppercase tracking-tight leading-none text-white mb-6">
            RESOURCES <span className="text-[#FFCC00]">&</span> REFERENCES
          </h2>
          <p className="text-base leading-relaxed text-white/60 mb-6">
            Key scientific studies and reports documenting the biological effects
            of electromagnetic field exposure on human health.
          </p>
          <div className="overflow-hidden h-64 border border-white/10">
            <img
              src={TOWER_IMG}
              alt="Cell tower"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Right column - resource list */}
        <div className="lg:col-span-2">
          <div className="space-y-0">
            {resources.map((res) => (
              <div
                key={res.url}
                className="border border-white/10 p-6 hover:bg-white/5 transition-colors duration-300 group"
                data-testid={`resource-item-${res.title}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-['Anton'] text-lg uppercase tracking-tight text-white mb-2 group-hover:text-[#FF3B30] transition-colors">
                      {res.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/50">{res.desc}</p>
                  </div>
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-white/30 hover:text-[#FF3B30] transition-colors"
                    data-testid={`resource-link-${res.url}`}
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════ */
/*  FAQ SECTION                                            */
/* ═══════════════════════════════════════════════════════ */
const faqItems = [
  {
    q: "What exactly is EMF and where does it come from?",
    a: "Electromagnetic fields (EMF) are invisible areas of energy produced by electrically charged objects. Sources include cell towers, Wi-Fi routers, power lines, smart meters, microwave ovens, Bluetooth devices, and all wireless technology. EMF exists on a spectrum from extremely low frequency (ELF) to radiofrequency (RF) to microwave radiation."
  },
  {
    q: "Is non-ionizing EMF really dangerous? Isn't that only ionizing radiation?",
    a: "While ionizing radiation (X-rays, gamma rays) has enough energy to break chemical bonds, non-ionizing EMF affects biology through different mechanisms — primarily by activating voltage-gated calcium channels (VGCCs), increasing oxidative stress, and disrupting cellular communication. The 'non-ionizing means safe' narrative is outdated and contradicted by thousands of peer-reviewed studies."
  },
  {
    q: "How can I measure EMF levels in my home?",
    a: "You can use an EMF meter (like the TriField TF2 or Acoustimeter AM-10) to measure electric fields, magnetic fields, and RF radiation. Key areas to check: near Wi-Fi routers, smart meters, electrical panels, and sleeping areas. Safe levels are generally considered below 0.2 μT for magnetic fields and below 0.1 V/m for RF."
  },
  {
    q: "What are the most common symptoms of EMF exposure?",
    a: "Commonly reported symptoms include chronic headaches, sleep disturbance, fatigue, difficulty concentrating, heart palpitations, tinnitus (ringing in ears), skin tingling or burning, anxiety, and depression. These symptoms are recognized as Electromagnetic Hypersensitivity (EHS), which is acknowledged by the WHO as a real condition."
  },
  {
    q: "How can I protect myself from EMF exposure?",
    a: "Key steps include: (1) Increase distance from EMF sources — distance is your best shield. (2) Turn off Wi-Fi at night. (3) Use wired connections instead of wireless when possible. (4) Keep phones away from your body. (5) Use EMF shielding materials for sleeping areas. (6) Ground/earth your body regularly. (7) Support your body's resilience with antioxidants, magnesium, and adequate sleep."
  },
  {
    q: "Are children more at risk from EMF exposure?",
    a: "Yes. Children's thinner skulls, developing nervous systems, higher water content in tissues, and rapidly dividing cells make them significantly more vulnerable to EMF effects. Multiple studies recommend limiting wireless device use for children, and some countries have banned Wi-Fi in preschools and primary schools."
  },
  {
    q: "What does 5G mean for EMF exposure?",
    a: "5G uses higher-frequency millimeter waves (24-100 GHz) in addition to existing frequencies. This requires far more cell antennas placed closer together. While millimeter waves don't penetrate as deeply, they are absorbed by skin and eyes, and the sheer density of infrastructure increases overall ambient EMF levels significantly. Long-term health studies on 5G frequencies are virtually nonexistent."
  }
];

const FAQSection = () => (
  <section id="faq" className="relative py-24 md:py-32" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-6">
      <div className="text-center mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
          Common Questions
        </p>
        <h2 className="font-['Anton'] text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white">
          FREQUENTLY <span className="text-[#FFCC00]">ASKED</span>
        </h2>
      </div>

      <Accordion type="single" collapsible className="space-y-0">
        {faqItems.map((item, i) => (
          <AccordionItem
            key={item.q}
            value={`faq-${i}`}
            className="border border-white/10 border-b-0 last:border-b px-6 data-[state=open]:bg-white/5"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger
              className="text-white text-left text-sm sm:text-base font-semibold hover:text-[#FF3B30] hover:no-underline py-5"
              data-testid={`faq-trigger-${i}`}
            >
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-white/60 text-sm leading-relaxed pb-6">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════ */
/*  NEWSLETTER SECTION                                     */
/* ═══════════════════════════════════════════════════════ */
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Check your inbox for a welcome message.");
      setEmail("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Subscription failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden"
      data-testid="newsletter-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img src={CTA_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <Mail className="w-10 h-10 text-[#FF3B30] mx-auto mb-6" />
        <h2 className="font-['Anton'] text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white mb-4">
          STAY <span className="text-[#FFCC00]">INFORMED</span>
        </h2>
        <p className="text-base leading-relaxed text-white/60 mb-10 max-w-lg mx-auto">
          Subscribe to receive updates on EMF research, protection strategies,
          and community stories. Knowledge is your first line of defense.
        </p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-0 max-w-lg mx-auto"
          data-testid="newsletter-form"
        >
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40 flex-1"
            required
            data-testid="newsletter-email-input"
          />
          <Button
            type="submit"
            disabled={loading}
            className="rounded-none uppercase tracking-widest font-bold bg-[#FF3B30] text-white border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 h-12 px-8"
            data-testid="newsletter-submit-btn"
          >
            {loading ? "..." : "SUBSCRIBE"}
          </Button>
        </form>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════ */
/*  CONTACT SECTION                                        */
/* ═══════════════════════════════════════════════════════ */
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0D0D0D]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left info */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
              Get In Touch
            </p>
            <h2 className="font-['Anton'] text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white mb-6">
              SHARE YOUR <span className="text-[#FFCC00]">EXPERIENCE</span>
            </h2>
            <p className="text-base leading-relaxed text-white/60 mb-8 max-w-md">
              Have you experienced EMF-related symptoms? Want to share your story
              or ask questions? Leave your message below and we will respond.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-white/50">
                <MessageCircle className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-sm">Your message is stored securely and reviewed personally</span>
              </div>
              <div className="flex items-center gap-4 text-white/50">
                <Mail className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-sm">We receive email notification for every submission</span>
              </div>
              <div className="flex items-center gap-4 text-white/50">
                <Shield className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-sm">Your privacy is respected — data never shared</span>
              </div>
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={handleSubmit}
            className="space-y-0"
            data-testid="contact-form"
          >
            <div className="border border-white/10 p-8 md:p-10 space-y-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">
                  Your Name
                </label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">
                  Your Email
                </label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
                  required
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">
                  Your Message
                </label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Share your experience or ask a question..."
                  rows={5}
                  className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 text-white placeholder:text-white/40 resize-none"
                  required
                  data-testid="contact-message-textarea"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-none uppercase tracking-widest font-bold bg-[#FF3B30] text-white border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 h-12 animate-pulse-glow"
                data-testid="contact-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? "SENDING..." : "SEND MESSAGE"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════ */
/*  FOOTER                                                 */
/* ═══════════════════════════════════════════════════════ */
const Footer = () => (
  <footer className="border-t border-white/10 py-12 px-6" data-testid="footer">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5 text-[#FF3B30]" />
        <span className="font-['Anton'] text-lg uppercase tracking-wider text-white">
          EMF<span className="text-[#FF3B30]">AWARE</span>
        </span>
      </div>
      <p className="text-xs text-white/30 text-center">
        This website is for educational and awareness purposes. Consult medical
        professionals for health concerns.
      </p>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="text-white/30 hover:text-[#FF3B30] transition-colors"
        data-testid="scroll-to-top-btn"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════════════ */
/*  MAIN PAGE                                              */
/* ═══════════════════════════════════════════════════════ */
const Home = () => (
  <div className="bg-[#0A0A0A] min-h-screen">
    <Header />
    <HeroSection />
    <StorySection />
    <MarqueeBanner />
    <ScienceSection />
    <div className="section-divider" />
    <ResourcesSection />
    <FAQSection />
    <NewsletterSection />
    <ContactSection />
    <Footer />
    <Toaster
      position="bottom-right"
      toastOptions={TOAST_OPTIONS}
    />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
